import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  TextField,
  Theme,
  Typography,
  useTheme
} from "@material-ui/core";
import clsx from 'clsx';
import React, {useEffect, useState} from "react";
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import Draggable from "react-draggable";
import { headerHeight } from "../app";
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { nanoid } from "@reduxjs/toolkit";
import { t } from "../localization";
import { isEqual } from 'lodash';

export interface DropDownInfo {
  value: string;
  title: string;
  entries: string[];
  text: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    draggableTitleClose: {
      color: theme.palette.grey[500],
      marginRight: theme.spacing(1)
    },
    draggableTitleMinimize: {
      color: theme.palette.grey[500],
    },
    staticContentHeight: {
      height: `min(75vh, 600px) !important`,
    },
    draggableContent: {
      padding: theme.spacing(0),
      paddingTop: "0px !important",
      height: "fit-content",
      width: "500px",
      maxWidth: "500px",
    },
    draggableContentSlim: {
      width: "400px",
      maxWidth: "400px"
    },
    draggableContentTiny: {
      width: "150px",
      maxWidth: "150px",
    },
    draggableContentResize: {
      height: "fit-content",
      width: "fit-content",
      maxWidth: 'none',
      maxHeight: 'none'
    },
    draggableContentParentBase: {
      display: "flex",
      flexDirection: "column"
    },
    draggableContentParentResize: {
      minWidth: "400px",
      maxWidth: "85vw",
      minHeight: "300px",
      maxHeight: "80vh",
      overflow: "hidden",
      position: "relative",
      resize: "both",
    },
    draggableContentParentStatic: {
      height: "100%"
    },
    draggableResizeHandler: {
      float: "right",
      position: "absolute",
      bottom: "0px",
      right: "0px"
    },
    draggableTopDiv: {
      display: "flex",
      alignItems: "center"
    },
    draggableBackdropPaper: {
      height: "inherit"
    },
    draggableBackdropRounded: {
      borderRadius: theme.spacing(0.5),
      height: "inherit",
    },
    draggableRoundCorner: {
      backgroundColor: "transparent"
    },
    draggableTitle: {
      userSelect: "none",
      cursor: "grab",
      flex: "1 !important"
    },
    draggableTitleDown: {
      cursor: "grabbing"
    },
    progressContent: {
      padding: `${theme.spacing(3)}px !important`,
    },
    progressNoTransition: {
      transition: "none"
    },
    dropdownGrid: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "& span": {
        flex: 1
      }
    },
    dropdownSpacing: {
      marginBottom: theme.spacing(2)
    },
  }),
);

export const PromptDialog = props => {
  const {open, title, prompt, button, onCancel, onSubmit} = props;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <DialogTitle>
        {title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          {prompt}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
        >
          {button}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AlertDialog = props => {
  const {open, title, handleClose} = props;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent>
        {props.children}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t("buttons.dismiss")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TextDialog = props => {
  const [text, setText] = useState("");
  const [disabled, setDisabled] = useState(true);

  const handleTextChange = (event) => {
    setText(event.target.value);
  };

  useEffect(() => {
    if (text.trim() !== '') {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [text]);

  useEffect(() => {
    setText(props.default);
  }, [props.open]);

  const handleCancel = () => {
    props.onCancel();
    setText(props.default);
  };

  const handleSubmit = () => {
    const trimmedText = text.trim();

    props.onSubmit(trimmedText);

    if (props.clear) {
      setText('');
    } else {
      setText(trimmedText);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (text === '') {
        return;
      }

      handleSubmit();
    }
  };

  return (
    <Dialog open={props.open} aria-labelledby="form-dialog-title">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {props.prompt}
        </DialogContentText>
        <TextField
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder={props.default}
          margin="dense"
          label={props.label}
          type="text"
          fullWidth
          value={text}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={disabled}>
          {t("buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const DropDownDialog = props => {
  const [value, setValue] = useState(props.values[0]);

  const resetValue = () => {
    setValue(props.values[0]);
  };

  const handleCancel = () => {
    props.onCancel();
    resetValue();
  };

  const handleSubmit = () => {
    props.onSubmit(value);
    resetValue();
  };

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  return (
    <Dialog open={props.open} aria-labelledby="form-dialog-title">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {props.prompt}
        </DialogContentText>
        <Select
          value={value}
          fullWidth
          onChange={handleChange}
        >
          {props.values.map((value, index) => {
            return (
              <MenuItem key={index} value={value}>{value}</MenuItem>
            );
          })}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary">
          {t("buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const DraggableDialog = props => {
  const {open, title, staticHeight, variant, removeClose,
    onClose, onMinimize, defaultPosition, customBounds} = props;

  const classes = useStyles();
  const theme = useTheme();

  const tiny = (variant === 'tiny');
  const narrow = (variant === 'narrow');
  const resize = (variant === 'resize');

  const paperPropID = useState(nanoid())[0];
  const resizeElementID = useState(nanoid())[0];

  const startX = defaultPosition ? defaultPosition.x : theme.spacing(2);
  const startY = defaultPosition ? defaultPosition.y : headerHeight + theme.spacing(2);
  const [position, setPosition] = useState({
    x: startX,
    y: startY
  });

  const [mouseDown, setMouseDown] = useState(false);
  const [resizeWidth, setResizeWidth] = useState(400);
  const [resizeHeight, setResizeHeight] = useState(300);
  const [bounds, setBounds] = useState(null);

  const onDragMouseDown = () => {
    setMouseDown(true);
  };

  const onDragMouseUp = () => {
    setMouseDown(false);
  };

  const getNewBoundary = (elementWidth) => {
    let newBounds = {
      right : customBounds
        ? window.innerWidth - customBounds.right - elementWidth
        : window.innerWidth - elementWidth,
      left : customBounds ? customBounds.left : 0,
      bottom : window.innerHeight - 40,
      top : headerHeight
    };

    if (newBounds.right - newBounds.left < elementWidth) {
      newBounds.right = window.innerWidth - elementWidth;
      newBounds.left = 0;
    }

    // Add bounds padding
    const boundsPadding = 10;
    newBounds.right -= boundsPadding;
    newBounds.left += boundsPadding;
    newBounds.top += boundsPadding;

    if (!isEqual(bounds, newBounds)) {
      setBounds(newBounds);
    }

    return newBounds;
  };

  const checkOutOfBounds = (newBounds) => {
    let x = position.x;
    let y = position.y;

    const rightOutOfBounds = (x > newBounds.right);
    const leftOutOfBounds = (x < newBounds.left);
    const bottomOutOfBounds = (y > newBounds.bottom);
    const topOutOfBounds = (y < newBounds.top);

    const anyOutOfBounds = rightOutOfBounds
      || leftOutOfBounds
      || topOutOfBounds
      || bottomOutOfBounds;

    if (!anyOutOfBounds) return;

    if (rightOutOfBounds) {
      x = Math.min(x, newBounds.right);
    }

    if (leftOutOfBounds) {
      x = Math.max(newBounds.left, x);
    }

    if (bottomOutOfBounds) {
      y = Math.min(y, newBounds.bottom);
    }

    if (topOutOfBounds) {
      y = Math.max(y, newBounds.top);
    }

    const newPosition = {x, y};
    setPosition(newPosition);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      let element = document.getElementById(resizeElementID);
      if (!element) return;

      // Remember our width and height so it can be applied
      // the next time that the dialog is opened
      const elementWidth = element.clientWidth;
      const elementHeight = element.clientHeight;
      setResizeWidth(elementWidth);
      setResizeHeight(elementHeight);

      // Calculate new boundary values
      const newBounds = getNewBoundary(elementWidth);

      // Move dialog if we are out of bounds
      checkOutOfBounds(newBounds);
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [bounds, position, customBounds, resizeWidth, resizeHeight]);

  // Apply most recent width/height
  const resizedStyle = resize ? {
    height: `${resizeHeight}px`,
    width: `${resizeWidth}px`
  } : {};

  return (
    <Draggable
      handle=".draggable-dialog-title"
      position={position}
      defaultPosition={defaultPosition}
      bounds={bounds}
      onDrag={(event, data) => {
        const {x,y} = data;
        setPosition({x, y});
      }}
    >
      <Dialog
        fullScreen
        maxWidth="lg"
        open={open}
        onFocus={() => {
          /** Remove tabindex from paper to stop drag/drop issues */
          let element = document.getElementById(paperPropID);
          element?.parentElement.removeAttribute("tabindex");
        }}
        aria-labelledby="draggable-dialog-title"
        className={clsx(classes.draggableContent, {
          [classes.draggableContentSlim]: narrow,
          [classes.draggableContentResize]: resize,
          [classes.staticContentHeight]: staticHeight,
          [classes.draggableContentTiny]: tiny
        })}
        classes={{
          paperFullScreen: classes.draggableBackdropRounded,
          scrollPaper: classes.draggableBackdropPaper
        }}
        BackdropProps={{
          className: classes.draggableRoundCorner
        }}
        PaperProps={{
          id: paperPropID
        }}
      >
        <div
          onMouseDown={onDragMouseDown}
          onMouseUp={onDragMouseUp}
          className={classes.draggableTopDiv}
        >
          {/* Draggable title */}
          <DialogTitle
            className={clsx("draggable-dialog-title", classes.draggableTitle, {
              [classes.draggableTitleDown]: mouseDown,
            })}
          >
            {title}
          </DialogTitle>

          {/* Optional minimize button */}
          {onMinimize && (<IconButton
            aria-label="minimize"
            className={classes.draggableTitleMinimize}
            onClick={onMinimize}
          >
            <RemoveIcon fontSize="small"/>
          </IconButton>)}

          {/* Close button */}
          {(!removeClose) && <IconButton
            aria-label="close"
            className={classes.draggableTitleClose}
            onClick={onClose}
          >
            <CloseIcon fontSize="small"/>
          </IconButton>}
        </div>

        <div
          id={resizeElementID}
          style={resizedStyle}
          className={clsx(classes.draggableContentParentBase, {
            [classes.draggableContentParentResize]: resize,
            [classes.draggableContentParentStatic]: !resize
          })}
        >
          {props.children}
        </div>

      </Dialog>
    </Draggable>
  );
};

export const ProgressDialog = props => {
  const {open, text, percent, noTransition} = props;
  const classes = useStyles();

  return (
    <Dialog open={open}>
      <DialogContent className={classes.progressContent}>
        <DialogContentText>
          {text} <b>{percent.toFixed(2)}%</b>
        </DialogContentText>
        <LinearProgress
          variant="determinate"
          value={percent}
          classes={{
            bar: clsx("", {
              [classes.progressNoTransition]: noTransition
            })
          }}
          color="secondary"
        />
      </DialogContent>
    </Dialog>
  );
};

export const MultiDropDownDialog = props => {
  const [dropDowns, setDropDowns] = useState(props.dropDowns);
  const classes = useStyles();

  const handleCancel = () => {
    props.onCancel();
  };

  useEffect(() => {
    let tempDropDowns = dropDowns;
    setDropDowns(tempDropDowns);
  }, []);

  const handleSubmit = () => {
    props.onSubmit(dropDowns);
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>, key) => {
    let tempDropDown: {[key: string]: DropDownInfo} = {...dropDowns};
    tempDropDown[key].value = event.target.value as string;
    setDropDowns(tempDropDown);
  };

  return (
    <Dialog fullWidth maxWidth={props.maxWidth} open={props.open} aria-labelledby="form-dialog-title">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={props.spacing} >
          {Object.keys(dropDowns).map(key => {
            let dropDown = dropDowns[key] as DropDownInfo;

            return (
              <Grid
                key={key}
                item
                xs={props.gridSize}
                className={classes.dropdownGrid}
              >
                <InputLabel id={dropDown.title}>
                  {dropDown.title}
                </InputLabel>

                <Typography variant="caption">
                  {dropDown.text}
                </Typography>

                <Select
                  fullWidth
                  id={dropDown.title + '_id'}
                  value={dropDown.value}
                  onChange={(event) => {
                    handleChange(event, key);
                  }}
                  className={classes.dropdownSpacing}
                >
                  {dropDown.entries.map(dropDownEntry =>
                    <MenuItem
                      value={dropDownEntry}
                      key={dropDownEntry + dropDown.title}
                    >
                      {dropDownEntry}
                    </MenuItem>
                  )}

                </Select>
              </Grid>
            );
          })}
        </Grid>

      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary">
          {t("buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
